import {
  Component,
  Element,
  Fragment,
  h,
  Prop,
  State,
  Watch,
} from "@stencil/core"
import {
  getAppearance,
  translate,
} from "../../core/helpers/webcomponent-helpers"

/**
 * Converts the amount of milliseconds given to a struct of days, hours, minutes and seconds
 *
 * @param duration the duration to convert, in milliseconds
 */
function extract(duration: number) {
  const result = {
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  }

  result.seconds = Math.ceil(duration / 1000)
  result.minutes = Math.floor(result.seconds / 60)
  result.seconds %= 60
  result.hours = Math.floor(result.minutes / 60)
  result.minutes %= 60
  result.days = Math.floor(result.hours / 24)
  result.hours %= 24

  return result
}

@Component({
  tag: "sdx-countdown",
  styleUrl: "countdown.scss",
  shadow: true,
})
export class Countdown {
  @Element() public el!: HTMLSdxCountdownElement
  @State() private days: string = ""
  @State() private hours: string = ""
  @State() private minutes: string = ""
  @State() private seconds: string = ""
  @State() private ended = true
  private updateTimer?: ReturnType<typeof setTimeout>
  private timestamp: number = 0

  /**
   * End date, in date time string format (YYYY-MM-DDTHH:mm:ss.sssZ).
   */
  @Prop() public date?: string

  /**
   * The mask that controls the display of the countdown.
   */
  @Prop() public mask: "DD-HH-MM-SS" | "HH-MM-SS" | "MM-SS" = "DD-HH-MM-SS"

  /**
   * The background variant.
   */
  @Prop() public background: "horizon" | "orchid" = "orchid"

  /**
   * The size variation.
   */
  @Prop() public size: "small" | "large" = "large"

  @Watch("date")
  public dateChanged() {
    this.timestamp = this.getTimestamp()
    clearTimeout(this.updateTimer)
    this.update()
  }

  public componentDidLoad() {
    this.dateChanged()
  }

  public disconnectedCallback() {
    clearTimeout(this.updateTimer)
  }

  private getTimestamp() {
    return Date.parse(
      this.date?.replace(/(\d+)[.](\d+)[.](\d+)/g, "$1-$2-$3") || "",
    )
  }

  private update() {
    const now = Date.now()
    this.ended = isNaN(this.timestamp) || this.timestamp < now

    if (this.ended) {
      return
    }

    const countdown = extract(this.timestamp - now)
    this.days = `${countdown.days}`.padStart(2, "0")
    this.hours = `${countdown.hours}`.padStart(2, "0")
    this.minutes = `${countdown.minutes}`.padStart(2, "0")
    this.seconds = `${countdown.seconds}`.padStart(2, "0")

    this.updateTimer = setTimeout(() => {
      this.update()
    }, 500)
  }

  private getComponentClassNames() {
    return {
      component: true,
      [this.background]: true,
      [getAppearance(this.el)]: true,
      [this.size]: true,
    }
  }

  private getTimeUntilCountdownEnd(): number {
    const currentDate = new Date()
    const targetDate = new Date(this.timestamp)
    const differenceInTime = targetDate.getTime() - currentDate.getTime()
    return differenceInTime / (1000 * 3600 * 24)
  }

  private getSrHint(): string {
    const srHint = [],
      days = parseInt(this.days, 10),
      hours = parseInt(this.hours, 10),
      minutes = parseInt(this.minutes, 10),
      seconds = parseInt(this.seconds, 10),
      i18n = (text: string) => translate(text, this.el.lang)

    srHint.push(i18n("Time remaining:"))

    if (this.mask.includes("DD")) {
      srHint.push(days, days === 1 ? i18n("Day") : i18n("Days"))
    }

    if (this.mask.includes("HH")) {
      srHint.push(hours, hours === 1 ? i18n("Hour") : i18n("Hours"))
    }

    srHint.push(minutes, minutes === 1 ? i18n("Minute") : i18n("Minutes"))

    srHint.push(i18n("and"))

    srHint.push(seconds, seconds === 1 ? i18n("Second") : i18n("Seconds"))

    return srHint.join(" ")
  }

  public render() {
    if (!this.date || this.ended || this.getTimeUntilCountdownEnd() > 7) {
      return
    }

    return (
      <Fragment>
        <div class={this.getComponentClassNames()} aria-hidden="true">
          {this.mask.includes("DD") && (
            <Fragment>
              <div class="unit-wrapper">
                <span class="number">{this.days}</span>
                <span class="unit">
                  {translate(this.days === "01" ? "Day" : "Days", this.el.lang)}
                </span>
              </div>
              <span class="separator">:</span>
            </Fragment>
          )}

          {this.mask.includes("HH") && (
            <Fragment>
              <div class="unit-wrapper">
                <span class="number">{this.hours}</span>
                <span class="unit">{translate("Hrs", this.el.lang)}</span>
              </div>
              <span class="separator">:</span>
            </Fragment>
          )}

          {
            <Fragment>
              <div class="unit-wrapper">
                <span class="number">{this.minutes}</span>
                <span class="unit">{translate("Min", this.el.lang)}</span>
              </div>
              <span class="separator">:</span>
            </Fragment>
          }

          {
            <Fragment>
              <div class="unit-wrapper">
                <span class="number">{this.seconds}</span>
                <span class="unit">{translate("Sec", this.el.lang)}</span>
              </div>
            </Fragment>
          }
        </div>
        <span class="sr-only" role="timer">
          {this.getSrHint()}
        </span>
      </Fragment>
    )
  }
}
